<template>
  <form
    :action="config.actionUrl"
    method="POST"
    class="space-y-5"
    @submit="onSubmit"
  >
    <input type="hidden" name="_token" :value="config.csrfToken" />
    <input type="hidden" name="timezone" :value="timezone" />
    <input type="hidden" name="locale" :value="config.locale" />

    <!-- Honeypot: hidden from humans, bots auto-fill it -->
    <div style="position:absolute;left:-9999px;opacity:0;" aria-hidden="true">
      <input
        type="text"
        name="website_url"
        v-model="honeypot"
        tabindex="-1"
        autocomplete="off"
      />
    </div>

    <!-- Server-side validation errors -->
    <div
      v-if="serverErrors.length"
      style="border-radius: 1rem; border: 1px solid #fecaca; background: #fef2f2; padding: 1rem; font-size: 0.875rem; color: #b91c1c;"
    >
      <ul style="list-style: disc; padding-left: 1.25rem;">
        <li
          v-for="error in serverErrors"
          :key="error"
          style="margin-bottom: 0.25rem;"
        >
          {{ error }}
        </li>
      </ul>
    </div>

    <!-- School Name -->
    <div>
      <label class="marketing-label" for="school_name">{{
        t("school_name")
      }}</label>
      <input
        id="school_name"
        type="text"
        name="school_name"
        v-model="schoolName"
        class="marketing-input"
        required
      />
      <p
        v-if="fieldError('school_name')"
        style="margin-top: 0.25rem; font-size: 0.75rem; color: #dc2626;"
      >
        {{ fieldError("school_name") }}
      </p>
    </div>

    <!-- Subdomain -->
    <div>
      <label class="marketing-label" for="subdomain">{{
        t("skolar_address")
      }}</label>
      <div
        class="flex overflow-hidden rounded-2xl border border-[var(--sk-border-strong)] bg-white shadow-sm focus-within:border-[var(--sk-coral)] focus-within:ring-4 focus-within:ring-[rgba(238,123,90,0.15)]"
      >
        <span
          class="shrink-0 border-r border-[var(--sk-border)] bg-[rgba(30,41,59,0.03)] px-4 py-3 text-sm font-semibold text-[var(--sk-muted)]"
          >https://</span
        >
        <input
          id="subdomain"
          type="text"
          name="subdomain"
          v-model="subdomain"
          @input="subdomainManuallyEdited = true"
          class="min-w-0 flex-1 border-0 bg-transparent px-4 py-3 text-sm text-[var(--sk-text)] outline-none"
          required
        />
        <span
          class="shrink-0 border-l border-[var(--sk-border)] bg-[rgba(30,41,59,0.03)] px-4 py-3 text-sm font-semibold text-[var(--sk-muted)]"
          >{{ config.domainSuffix }}</span
        >
      </div>
      <p
        v-if="fieldError('subdomain')"
        style="margin-top: 0.25rem; font-size: 0.75rem; color: #dc2626;"
      >
        {{ fieldError("subdomain") }}
      </p>
    </div>

    <!-- Contact Name -->
    <div>
      <label class="marketing-label" for="contact_name">{{
        t("full_name")
      }}</label>
      <input
        id="contact_name"
        type="text"
        name="contact_name"
        v-model="contactName"
        class="marketing-input"
        required
      />
      <p
        v-if="fieldError('contact_name')"
        style="margin-top: 0.25rem; font-size: 0.75rem; color: #dc2626;"
      >
        {{ fieldError("contact_name") }}
      </p>
    </div>

    <!-- Email -->
    <div>
      <label class="marketing-label" for="email2">{{ t("email") }}</label>
      <input
        id="email2"
        type="email"
        name="email2"
        v-model="email"
        class="marketing-input"
        required
      />
      <p
        v-if="fieldError('email2')"
        style="margin-top: 0.25rem; font-size: 0.75rem; color: #dc2626;"
      >
        {{ fieldError("email2") }}
      </p>
    </div>

    <!-- Phone -->
    <div>
      <label class="marketing-label" for="phone_number">{{
        personalWhatsappLabel
      }}</label>
      <p class="mb-2 text-xs leading-5 text-[var(--sk-muted)]">
        {{ personalWhatsappHelp }}
      </p>
      <PhoneInput
        ref="phoneInput"
        :initial-value="config.oldInput?.phone_number || ''"
        input-class="marketing-input"
        @input="phoneValidationError = ''"
      />
      <p
        v-if="fieldError('phone_number')"
        style="margin-top: 0.25rem; font-size: 0.75rem; color: #dc2626;"
      >
        {{ fieldError("phone_number") }}
      </p>
    </div>

    <!-- Password with show/hide toggle -->
    <div>
      <label class="marketing-label" for="password">{{ t("password") }}</label>
      <div class="relative">
        <input
          id="password"
          :type="showPassword ? 'text' : 'password'"
          name="password"
          v-model="password"
          class="marketing-input pr-16"
          required
          minlength="6"
        />
        <button
          type="button"
          @click="showPassword = !showPassword"
          class="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-[var(--sk-muted)] hover:text-[var(--sk-text)]"
        >
          {{ showPassword ? t("hide_password") : t("show_password") }}
        </button>
      </div>
      <p
        v-if="fieldError('password')"
        style="margin-top: 0.25rem; font-size: 0.75rem; color: #dc2626;"
      >
        {{ fieldError("password") }}
      </p>
    </div>

    <!-- Turnstile -->
    <div v-if="config.turnstileSiteKey" ref="turnstileContainer"></div>

    <button
      class="marketing-button-primary w-full border-0"
      type="submit"
      :disabled="submitting"
    >
      {{ submitting ? "..." : t("create_account") }}
    </button>

    <p
      class="text-sm leading-7 text-[var(--sk-muted)]"
      v-html="t('accept_terms')"
    ></p>

    <div class="flex justify-end">
      <a
        :href="config.helpUrl"
        class="text-sm font-semibold text-[var(--sk-muted)] transition hover:text-[var(--sk-text)]"
      >
        {{ t("help") }}
      </a>
    </div>
  </form>
</template>

<script>
import PhoneInput from "./PhoneInput.vue";

export default {
  name: "RegistrationForm",
  components: { PhoneInput },
  props: {
    config: { type: Object, required: true }
  },
  data() {
    return {
      schoolName: this.config.oldInput?.school_name || "",
      subdomain: this.config.oldInput?.subdomain || "",
      subdomainManuallyEdited: !!this.config.oldInput?.subdomain,
      contactName: this.config.oldInput?.contact_name || "",
      email: this.config.oldInput?.email2 || "",
      password: "",
      showPassword: false,
      honeypot: "",
      timezone: "",
      submitting: false,
      phoneValidationError: ""
    };
  },
  computed: {
    isSpanishLocale() {
      return (this.config.locale || "").toLowerCase().startsWith("es");
    },
    personalWhatsappLabel() {
      return this.isSpanishLocale
        ? "WhatsApp personal"
        : "Personal WhatsApp number";
    },
    personalWhatsappHelp() {
      return this.isSpanishLocale
        ? "Usa el número donde recibes mensajes de WhatsApp personalmente."
        : "Use the number where you personally receive WhatsApp messages.";
    },
    serverErrors() {
      if (!this.config.errors) return [];
      const all = [];
      Object.values(this.config.errors).forEach(msgs => {
        if (Array.isArray(msgs)) {
          all.push(...msgs);
        } else if (typeof msgs === "string") {
          all.push(msgs);
        }
      });
      return all;
    }
  },
  watch: {
    schoolName(name) {
      if (!this.subdomainManuallyEdited) {
        this.subdomain = name
          .toLowerCase()
          .replace(/[^a-z0-9]/g, "")
          .slice(0, 20);
      }
    }
  },
  mounted() {
    this.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    this.loadTurnstile();
  },
  methods: {
    t(key) {
      return this.config.translations?.[key] || key;
    },
    fieldError(field) {
      if (field === "phone_number" && this.phoneValidationError) {
        return this.phoneValidationError;
      }
      const errs = this.config.errors?.[field];
      if (!errs) return "";
      return Array.isArray(errs) ? errs[0] : errs;
    },
    onSubmit(e) {
      if (this.submitting) {
        e.preventDefault();
        return;
      }

      if (!this.$refs.phoneInput?.isValidMobileNumber()) {
        e.preventDefault();
        this.phoneValidationError = this.t("phone_mobile_invalid");
        this.$refs.phoneInput?.focus();
        return;
      }

      this.submitting = true;
      // Form submits normally as HTML POST
    },
    loadTurnstile() {
      if (!this.config.turnstileSiteKey) return;

      const script = document.createElement("script");
      script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js";
      script.async = true;
      script.defer = true;
      script.onload = () => {
        if (window.turnstile && this.$refs.turnstileContainer) {
          window.turnstile.render(this.$refs.turnstileContainer, {
            sitekey: this.config.turnstileSiteKey,
            callback: () => {}
          });
        }
      };
      document.head.appendChild(script);
    }
  }
};
</script>
